/**
 * Hook for managing user inactivity tracking and automatic logout.
 * Provides functionality to:
 * - Track user interactions (mouse, keyboard, touch)
 * - Automatically log out inactive users
 * - Handle JWT token refresh before expiration
 * - Manage interaction timestamps in localStorage
 */

import { useEffect, useCallback, useRef } from 'react';
import { useAuthStore } from '@/stores/authStore';
import { useSystemConfig } from '@/hooks/useSystemConfig';
import { log } from '@/lib/utils/logger';
import { scopedLocalStorage } from '@/lib/browser/scopedStorage';
import { userHasEppn } from '@/lib/auth/sessionAuth';

/**
 * Structure of the JWT data stored in localStorage.
 * Used specifically for checking token expiration in the inactivity manager.
 */
interface JwtData {
  expiration: string;
  [key: string]: any;
}

/**
 * Configuration settings for inactivity management.
 * Controls timing and behavior of the inactivity tracking system.
 */
interface InactivityConfig {
  /** Logical key (scoped by base path) for last interaction timestamp */
  lastInteractionLogicalKey: string;
  /** Default session timeout in hours if not specified in system config */
  defaultTimeoutHours: number;
  /** Time before JWT expiration to trigger refresh (in milliseconds) */
  jwtRefreshThresholdMs: number;
  /** Debounce delay for interaction events (in milliseconds) */
  interactionDebounceMs: number;
}

const INACTIVITY_CONFIG: InactivityConfig = {
  lastInteractionLogicalKey: 'lastInteraction',
  defaultTimeoutHours: 8,
  jwtRefreshThresholdMs: 2 * 60 * 1000, // 2 minutes before expiration
  interactionDebounceMs: 5000, // 5 seconds debounce for interaction updates
};

/**
 * Interface for managing user inactivity.
 * Provides methods to manually control interaction tracking.
 */
interface InactivityManager {
  /** Updates the last interaction timestamp in localStorage */
  updateLastInteraction: () => void;
  /** Removes stored interaction data from localStorage */
  clearInteractionData: () => void;
}

/**
 * Custom hook that manages user session inactivity and JWT token refresh.
 *
 * Features:
 * - Tracks user interactions with debouncing to prevent excessive updates
 * - Automatically logs out users after configured inactivity period
 * - Refreshes JWT tokens before they expire if user is active (reusing MSAL tokens when available)
 * - Handles cleanup of event listeners and timeouts
 */
export function useInactivityManager(): InactivityManager {
  const { currentUser, logout, getJwt, fetchUserDataFromServer, getToken, notifySessionLost } = useAuthStore();
  const systemConfig = useSystemConfig();
  const nextCheckTimeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);
  const checkInactivityAndJwtRef = useRef<(() => Promise<void>) | undefined>(undefined);

  /**
   * Updates the last interaction timestamp in localStorage.
   * Only updates if we're in a browser environment and have a current user.
   */
  const updateLastInteraction = useCallback(() => {
    if (typeof window === 'undefined' || !currentUser) return;
    scopedLocalStorage.setItem(INACTIVITY_CONFIG.lastInteractionLogicalKey, new Date().toISOString());
  }, [currentUser]);

  /**
   * Removes the interaction timestamp from localStorage.
   * Used during logout or when resetting the inactivity tracking.
   */
  const clearInteractionData = useCallback(() => {
    if (typeof window === 'undefined') return;
    scopedLocalStorage.removeItem(INACTIVITY_CONFIG.lastInteractionLogicalKey);
  }, []);

  /**
   * Schedules the next inactivity/JWT check.
   * Uses a ref to avoid circular dependencies with useCallback.
   */
  const scheduleNextCheck = useCallback((delayMs: number) => {
    // Clear any existing scheduled check
    if (nextCheckTimeoutRef.current) {
      clearTimeout(nextCheckTimeoutRef.current);
    }

    if (delayMs > 0 && checkInactivityAndJwtRef.current) {
      nextCheckTimeoutRef.current = setTimeout(() => {
        checkInactivityAndJwtRef.current?.();
      }, delayMs);
    }
  }, []);

  /**
   * Checks and handles both user inactivity and JWT token expiration.
   *
   * Process:
   * 1. Verifies last interaction time against timeout threshold
   * 2. Logs out user if inactive beyond timeout
   * 3. Checks JWT expiration if user is active
   * 4. Refreshes JWT if approaching expiration
   * 5. Schedules next check based on earlier of JWT or inactivity deadline
   * 6. Signs the user out if refresh fails and either the JWT is expired or inactivity timeout is reached
   */
  const checkInactivityAndJwt = useCallback(async () => {
    if (typeof window === 'undefined' || !currentUser) return;

    // Check inactivity first
    const lastInteraction = scopedLocalStorage.getItem(INACTIVITY_CONFIG.lastInteractionLogicalKey);
    if (!lastInteraction) {
      log.debug('[InactivityManager] No last interaction found, setting initial interaction time');
      updateLastInteraction();
      scheduleNextCheck(INACTIVITY_CONFIG.interactionDebounceMs);
      return;
    }

    const timeoutHours = systemConfig?.sessionTimeoutInHours || INACTIVITY_CONFIG.defaultTimeoutHours;
    const timeoutMs = timeoutHours * 60 * 60 * 1000;
    const lastInteractionTime = new Date(lastInteraction).getTime();
    const currentTime = Date.now();
    const timeUntilInactivityTimeout = lastInteractionTime + timeoutMs - currentTime;

    log.debug('[InactivityManager] Inactivity check', () => ({
      timeUntilTimeoutSeconds: Math.round(timeUntilInactivityTimeout / 1000),
      lastInteractionIso: new Date(lastInteractionTime).toISOString(),
    }));

    // If user is inactive, log them out
    if (timeUntilInactivityTimeout <= 0) {
      log.info(
        `[InactivityManager] Logging out due to inactivity (${timeoutHours} hour timeout). Last interaction: ${lastInteraction}`
      );
      const wasSignedIn = userHasEppn(useAuthStore.getState().currentUser);
      clearInteractionData();
      await logout({ scope: 'list', redirectToHome: false });
      if (wasSignedIn) {
        notifySessionLost('inactivity');
      }
      return;
    }

    // If user is active, check JWT expiration
    try {
      const userData = JSON.parse(scopedLocalStorage.getItem('user-data') || '{}') as JwtData;
      const jwtExpiration = userData?.expiration ? new Date(userData.expiration).getTime() : null;

      if (jwtExpiration) {
        let effectiveJwtExpiration = jwtExpiration;
        let timeUntilExpiration = effectiveJwtExpiration - currentTime;
        log.debug('[InactivityManager] JWT check', () => ({
          timeUntilExpirationSeconds: Math.round(timeUntilExpiration / 1000),
          expirationIso: new Date(effectiveJwtExpiration).toISOString(),
        }));

        // Only refresh JWT if it's about to expire and user is active
        if (timeUntilExpiration <= INACTIVITY_CONFIG.jwtRefreshThresholdMs && timeUntilInactivityTimeout > 0) {
          log.info('[InactivityManager] JWT approaching expiration, attempting refresh', () => ({
            secondsRemaining: Math.round(timeUntilExpiration / 1000),
          }));
          try {
            const wasSignedIn = userHasEppn(currentUser);
            const newExpiration = await getJwt(getToken);
            if (newExpiration) {
              await fetchUserDataFromServer({ allowCommunityFallback: false });
              if (wasSignedIn && !userHasEppn(useAuthStore.getState().currentUser)) {
                notifySessionLost('degraded');
                return;
              }
              log.info(
                `[InactivityManager] JWT refreshed successfully. New expiration: ${new Date(newExpiration).toISOString()}`
              );
              const refreshedExpirationTime = new Date(newExpiration).getTime();
              if (!Number.isNaN(refreshedExpirationTime)) {
                effectiveJwtExpiration = refreshedExpirationTime;
                timeUntilExpiration = refreshedExpirationTime - Date.now();
              }
              updateLastInteraction();
            } else {
              log.warn('[InactivityManager] JWT refresh failed, but user is still active. Will retry on next check.');
            }
          } catch (error) {
            log.error('[InactivityManager] Error during JWT refresh:', error);
            const now = Date.now();
            const isJwtExpired = effectiveJwtExpiration <= now;
            if (isJwtExpired || timeUntilInactivityTimeout <= 0) {
              const wasSignedIn = userHasEppn(useAuthStore.getState().currentUser);
              log.info('[InactivityManager] Logging out due to JWT refresh failure', {
                reason: isJwtExpired ? 'jwt-expired' : 'inactivity-timeout',
              });
              clearInteractionData();
              await logout({ scope: 'list', redirectToHome: false });
              if (wasSignedIn) {
                notifySessionLost(isJwtExpired ? 'degraded' : 'inactivity');
              }
              return;
            }
          }
        }

        // Schedule next check based on the earlier of JWT expiration or inactivity timeout
        const nextJwtCheckTime = timeUntilExpiration - INACTIVITY_CONFIG.jwtRefreshThresholdMs;
        const nextCheckByJwt = nextJwtCheckTime > 0 ? nextJwtCheckTime : timeUntilExpiration;
        const nextCheckByInactivity = timeUntilInactivityTimeout;
        const nextCheckTime = Math.min(nextCheckByJwt, nextCheckByInactivity);
        const nextCheckReason = nextCheckTime === nextCheckByJwt ? 'jwt-window' : 'inactivity-window';

        if (nextCheckTime > 0) {
          log.debug('[InactivityManager] Scheduling next check', () => ({
            nextCheckSeconds: Math.round(nextCheckTime / 1000),
            trigger: nextCheckReason,
            jwtWindowSeconds: Math.round(nextCheckByJwt / 1000),
            inactivityWindowSeconds: Math.round(nextCheckByInactivity / 1000),
          }));
          scheduleNextCheck(nextCheckTime);
        }
      }
    } catch (error) {
      log.error('[InactivityManager] Error checking JWT expiration:', error);
      // Only log out if user is inactive
      if (timeUntilInactivityTimeout <= 0) {
        const wasSignedIn = userHasEppn(useAuthStore.getState().currentUser);
        void logout({ scope: 'list', redirectToHome: false }).then(() => {
          if (wasSignedIn) {
            notifySessionLost('inactivity');
          }
        });
      }
    }
  }, [
    currentUser,
    systemConfig?.sessionTimeoutInHours,
    updateLastInteraction,
    logout,
    getJwt,
    getToken,
    fetchUserDataFromServer,
    clearInteractionData,
    scheduleNextCheck,
    notifySessionLost,
  ]);

  // Keep ref in sync with latest function
  useEffect(() => {
    checkInactivityAndJwtRef.current = checkInactivityAndJwt;
  }, [checkInactivityAndJwt]);

  /**
   * Sets up event listeners for tracking user interaction.
   *
   * Implements:
   * - Debounced event handling to prevent excessive updates
   * - Multiple interaction types (mouse, keyboard, touch, scroll)
   * - Automatic cleanup of listeners and timeouts
   */
  const setupInteractionListeners = useCallback(() => {
    if (typeof window === 'undefined' || !currentUser) return;

    let debounceTimeout: NodeJS.Timeout | undefined;

    // Update on user interactions with debouncing
    const updateOnInteraction = () => {
      if (debounceTimeout) {
        clearTimeout(debounceTimeout);
      }
      debounceTimeout = setTimeout(() => {
        updateLastInteraction();
      }, INACTIVITY_CONFIG.interactionDebounceMs);
    };

    // Track user interactions
    const events = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart'] as const;
    events.forEach((event) => {
      window.addEventListener(event, updateOnInteraction);
    });

    // Initial interaction update and check
    updateLastInteraction();
    checkInactivityAndJwt();

    return () => {
      events.forEach((event) => {
        window.removeEventListener(event, updateOnInteraction);
      });
      if (nextCheckTimeoutRef.current) {
        clearTimeout(nextCheckTimeoutRef.current);
      }
      if (debounceTimeout) {
        clearTimeout(debounceTimeout);
      }
    };
  }, [currentUser, updateLastInteraction, checkInactivityAndJwt]);

  // Set up interaction listeners
  useEffect(() => {
    return setupInteractionListeners();
  }, [setupInteractionListeners]);

  return {
    updateLastInteraction,
    clearInteractionData,
  };
}
